import { DatePipe } from '@angular/common';
import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, filter, first, map, Observable, Subject, takeUntil, tap } from 'rxjs';
import { LgvModel } from '../../data-models/longevity.model';
import { LgvAPIService } from '../services/lgv-api.service';
import { LgvClientService } from '../services/lgv-client.service';
import { LgvCompanyService } from '../services/lgv-company.service';
import { LgvTSHelper } from './ts-helper';

@Injectable({
    providedIn: 'root'
})
export class LgvSnapshotHelper implements OnDestroy {
    LA: LgvModel.LoanAnalysis;
    Property: LgvModel.Property;
    Loan: LgvModel.Loan;

    _TotalLoanBalance = 0;
    TotalLoanBalance$ = new BehaviorSubject(this._TotalLoanBalance);

    _Equity = 0;
    Equity$ = new BehaviorSubject(this._Equity);

    MVPieChartData: any = null;
    MVLineChartData: any = null;
    PastPmtChartData: any = null;
    FuturePmtChartData: any = null;
    ShortTermRentalData: any = null;
    LongTermRentalData: any = null;
    YTDPmtChartData: any = null;
    LastYearPmtChartData: any = null;

    _ColorScheme = { domain: [] };
    ColorScheme$ = new BehaviorSubject(this._ColorScheme);


    xAxisTicks = ['01-01-2019', '12-01-2019'];
    yScaleMin = 900000;
    bFalse = false;
    bTrue = true;

    CompanyCode: string = 'RPM';

    onDestroy$ = new Subject();

    constructor(private LgvApiSvc: LgvAPIService,
        private LgvClientSvc: LgvClientService,
        private lgvCompanySvc: LgvCompanyService,
        private datePipe: DatePipe
    ) {

        this.LgvClientSvc.SelectedProperty$.pipe(takeUntil(this.onDestroy$)).subscribe(p => {
            if (p !== null) {

                this.LgvClientSvc.SelectedClientLoan$.pipe(first(f => f !== null)).subscribe(x => {

                    if (!!x) {
                        this.Loan = x;
                        this.LA = x.loanAnalysis;
                        this.Property = p;
                        this.InitAfterSnapshot();
                    }
                });
            }
        });

        this.lgvCompanySvc.activeCompanyBranding$.pipe(filter(f=> !!f)).subscribe(x => {

            let primaryColor: LgvModel.BrandingAttribute = lgvCompanySvc.getBrandingAttribute(lgvCompanySvc.primaryColorKey, x?.attributes);
            let secondaryColor: LgvModel.BrandingAttribute = lgvCompanySvc.getBrandingAttribute(lgvCompanySvc.secondaryColorKey, x?.attributes);

            //kxk: todo - will also need to set $gColor1 and $gColor2 to make the doughnut graph and legend match
            this.ColorScheme$.next({
                domain: [primaryColor.value, secondaryColor.value]
            });
        });

    }

    ngOnDestroy(): void {
        this.onDestroy$.complete();
    }

    InitAfterSnapshot() {
        // Equity calculation requires us to look at all loan balances (active loans)
        let tmpEquity = 0;
        let tmpTotalLoanBalance = 0;

        if (this.Property && this.Property.loans) {
            this.Property.loans.forEach(l => {
                if (l.loanInfo.isActive && !!l.loanAnalysis) {
                    tmpTotalLoanBalance += l.loanAnalysis.estimatedBalance;
                }
            });
        }

        if (this.Property && this.Property.propertyInfo && this.Property.propertyInfo.avmestimatedValue) {
            tmpEquity = this.Property.propertyInfo.avmestimatedValue - tmpTotalLoanBalance;
        }

        this.TotalLoanBalance$.next(tmpTotalLoanBalance);
        this.Equity$.next(tmpEquity);

        this.MVPieChartData =
            [
                { "name": "Equity", "value": this.Equity },
                { "name": "Debt", "value": this.TotalLoanBalance$.value }
            ];

        this.PastPmtChartData =
            [
                { "name": "Principal", "value": this.LA.totalPrincipal },
                { "name": "Interest", "value": this.LA.totalInterest }
            ];

        this.FuturePmtChartData =
            [
                { "name": "Principal", "value": this.LA.remainingPrincipal },
                { "name": "Interest", "value": this.LA.remainingInterest }
            ];
        this.YTDPmtChartData =
            [
                { "name": "Principal", "value": this.LA.yearToDatePrincipal },
                { "name": "Interest", "value": this.LA.yearToDateInterest }
            ];

        this.LastYearPmtChartData =
            [
                { "name": "Principal", "value": this.LA.lastYearPrincipal },
                { "name": "Interest", "value": this.LA.lastYearInterest }
            ];

        //this.SetCompanyColors(this.LgvCompanyConfigSvc.Company);
    }


    //SetCompanyColors(config: LgvModel.CompanyConfig) {

    //    this.ColorScheme$.next({
    //        domain: config.colorScheme
    //    });
    //}

    // some properties don't update w/ out observables.  Gradually converting some properties to observables using LA$
    get LA$(): Observable<LgvModel.LoanAnalysis> { return this.LgvClientSvc.SelectedClientLoan$.pipe(takeUntil(this.onDestroy$), filter(f => f !== null), map(x => { return x === null ? null : x.loanAnalysis; })) }
    //get Equity$(): Observable<number> { return this.LA$.pipe(takeUntil(this.onDestroy$), filter(f => f !== null), map(x => { return x === null || x.estimatedBalance === null || x.estimatedHomeValue === null ? null : x.estimatedHomeValue - x.estimatedBalance; })) }

    get CurrentHomeValue(): number {
        try { return this.Property.propertyInfo.avmestimatedValue; }
        catch (ex) { return 0; }
    }

    get EstimatedBalance(): number {
        try { return this.TotalLoanBalance$.value }
        catch (ex) { return 0; }
    }

    get Equity(): number {
        try { return this.Equity$.value; }
        catch (ex) { return 0; }
    }

    get LoanTermYears(): number {
        try { return this.LA.loanTerm / 12; }
        catch (ex) { return 0; }
    }

    get LastYearTotalPayments(): number {
        try { return this.LA.lastYearInterest + this.LA.lastYearPrincipal; }
        catch (ex) { return 0; }
    }
    get LastYearPctPrincipal(): number {
        try { return (this.LA.lastYearPrincipal / this.LastYearTotalPayments) * 100; }
        catch (ex) { return 0; }
    }
    get LastYearPctInterest(): number {
        try { return (this.LA.lastYearInterest / this.LastYearTotalPayments) * 100; }
        catch (ex) { return 0; }
    }
    get YTDTotalPayments(): number {
        try { return this.LA.yearToDatePrincipal + this.LA.yearToDateInterest; }
        catch (ex) { return 0; }
    }
    get YTDPctPrincipal(): number {
        try { return (this.LA.yearToDatePrincipal / this.YTDTotalPayments) * 100; }
        catch (ex) { return 0; }
    }
    get YTDPctInterest(): number {
        try { return (this.LA.yearToDateInterest / this.YTDTotalPayments) * 100; }
        catch (ex) { return 0; }
    }
    get PctEquity(): number {
        try { return (this.Equity / this.CurrentHomeValue) * 100; }
        catch (ex) { return 0; }
    }
    get PctDebt(): number {
        try { return (this.TotalLoanBalance$.value / this.CurrentHomeValue) * 100; }
        catch (ex) { return 0; }
    }
    get TotalPaymentsRemaining(): number {
        try { return this.LA.remainingInterest + this.LA.remainingPrincipal; }
        catch (ex) { return 0; }
    }
    get CashOutAmt(): number {

        try {
            // Take the lesser of cash out amt to get to 80% LTV and 70% of Equity
            let tmp80PctLTVCashOut = (this.Property.propertyInfo.avmestimatedValue * .8) - this.EstimatedBalance;
            let tmp70PctEquityAmt = this.Equity * .7;
            let result = Math.min(tmp80PctLTVCashOut, tmp70PctEquityAmt);
            return result > 0 ? result : 0;
        }
        catch (ex) { return 0; }
    }
    get InvestmentMarketValue(): number {
        try { return this.CashOutAmt / .3; }
        catch (ex) { return 0; }
    }
    get PastTotalPayments(): number {
        try { return this.LA.totalInterest + this.LA.totalPrincipal; }
        catch (ex) { return 0; }
    }
    get PastPctPrincipal(): number {
        try { return (this.LA.totalPrincipal / this.PastTotalPayments) * 100; }
        catch (ex) { return 0; }
    }
    get PastPctInterest(): number {
        try { return (this.LA.totalInterest / this.PastTotalPayments) * 100; }
        catch (ex) { return 0; }
    }
    get FutureTotalPayments(): number {
        try { return this.LA.remainingInterest + this.LA.remainingPrincipal; }
        catch (ex) { return 0; }
    }
    get FuturePctPrincipal(): number {
        try { return (this.LA.remainingPrincipal / this.FutureTotalPayments) * 100; }
        catch (ex) { return 0; }
    }
    get FuturePctInterest(): number {
        try { return (this.LA.remainingInterest / this.FutureTotalPayments) * 100; }
        catch (ex) { return 0; }
    }
    get NumberPaymentsRemaining(): number {
        try {
            if (!this.Loan.loanInfo.lastPaymentDate) {
                return 0;
            }
            let lastPmt = LgvTSHelper.jsonDateToJSDate(this.Loan.loanInfo.lastPaymentDate);
            let today: Date = new Date();
            let numMonths = LgvTSHelper.monthDiff(today, lastPmt);
            //console.log('Num Months: ' + numMonths);
            return numMonths;
        }
        catch (ex) { console.log(ex); return 0; }
    }

}
